import type { Referrer } from '@/components/Approval/@components/ReferrerField';
import {
  buildContractAttributes,
  isContractApprovalType,
} from '@/utils/approval/contractIds';
import {
  buildCancelAttributes,
  isCancelApprovalType,
  type CancelTargetSnapshot,
} from '@/utils/approval/cancelApproval';
import {
  buildSaVoucherAttributes,
  isSaVoucherApprovalType,
  type SaVoucherSnapshot,
} from '@/utils/approval/saVoucher';
import {
  isExpensePreApprovalType,
  type ExpensePreApprovalState,
} from '@/utils/approval/expensePreApproval';
import {
  isOpenConstructionCostType,
  type OpenConstructionCostAttributes,
} from '@/utils/approval/openConstructionCost';
import { isFncoPreMarketingType } from '@/utils/approval/fncoPreMarketing';
import { isMarketingExpenseType } from '@/utils/approval/marketingExpense';
import {
  buildEntExpenseAttributes,
  isEntExpenseType,
  type EntExpenseState,
} from '@/utils/approval/entExpense';
import {
  buildWorkSpecificationAttributes,
  isWorkSpecificationType,
} from '@/utils/approval/workSpecification';
import {
  buildFncoShowRoomAttributes,
  isFncoShowRoomType,
} from '@/utils/approval/fncoShowRoom';
import {
  buildFncoProductPurchaseAttributes,
  isFncoProductPurchaseType,
} from '@/utils/approval/fncoProductPurchase';
import {
  buildVehicleCostAttributes,
  isVehicleCostType,
} from '@/utils/approval/vehicleCost';
import { isResignationType } from '@/utils/approval/resignation';
import { isLongServiceLeaveType } from '@/utils/approval/longServiceLeave';
import { isNewCorporateCardType } from '@/utils/approval/newCorporateCard';
import { isBpRegistrationType } from '@/utils/approval/businessPartnerRegistration';
import {
  buildCorporateSealAttributes,
  isCorporateSealType,
  type CorporateSealFormState,
} from '@/utils/approval/corporateSeal';
import {
  buildShopManagerFeeAttributes,
  isShopManagerFeeType,
  type ShopManagerFeeState,
} from '@/utils/approval/shopManagerFee';
import { isSapContractFeeType } from '@/utils/approval/sapContractFee';
import { isSerpStockAdjustmentType } from '@/utils/approval/serpStockAdjustment';
import { isSerpStoreChangeType } from '@/utils/approval/serpStoreChange';
import { isSerpStoreOpenType } from '@/utils/approval/serpStoreOpen';
import { isSerpStoreReturnType } from '@/utils/approval/serpStoreReturn';
import { isSerpLendingDelayType } from '@/utils/approval/serpLendingDelay';
import { isSerpStoreClosureType } from '@/utils/approval/serpStoreClosure';
import { isSerpIntermediaryReplaceType } from '@/utils/approval/serpIntermediaryReplace';
import { isSerpSellingPriceType } from '@/utils/approval/serpSellingPrice';
import { isSerpSeasonPlanType } from '@/utils/approval/serpSeasonPlan';
import { isSerpBulkStockAdjustmentType } from '@/utils/approval/serpBulkStockAdjustment';
import { isSerpSeasonEndType } from '@/utils/approval/serpSeasonEnd';
import { isSerpApStockTransferType } from '@/utils/approval/serpApStockTransfer';
import {
  buildMortgageFeeAttributes,
  isMortgageFeeType,
  type MortgageFeeFormState,
} from '@/utils/approval/mortgageFee';
import {
  buildStoreOpenAttributes,
  isStoreOpenType,
  type StoreOpenFormState,
} from '@/utils/approval/storeOpen';
import {
  buildCorporateCardRequestAttributes,
  isCorporateCardRequestType,
  type CorporateCardRequestFormState,
} from '@/utils/approval/corporateCardRequest';
import {
  buildAnnualLeaveRequestAttributes,
  isAnnualLeaveRequestType,
  type AnnualLeaveRequestFormState,
} from '@/utils/approval/annualLeaveRequest';
import {
  buildProbationEvaluationAttributes,
  isProbationEvaluationType,
  type ProbationEvaluationState,
} from '@/utils/approval/probationEvaluation';
import { isSalesBrandPermissionType } from '@/utils/approval/salesBrandPermission';
import {
  buildGroupSalesAttributes,
  isGroupSalesType,
  type GroupSalesState,
} from '@/utils/approval/groupSales';
import {
  buildMarketingCaseAttributes,
  buildMarketingCaseLink,
  isMarketingCaseType,
  type MarketingCaseState,
} from '@/utils/approval/marketingCase';
import { isProductionVendorType } from '@/utils/approval/productionVendor';
import { isProductLifecycleType } from '@/utils/approval/productLifecycle';
import { isStyleMasterPriceType } from '@/utils/approval/styleMasterPrice';
import { isProductionCostClosingType } from '@/utils/approval/productionCostClosing';
import { isClaimClosingType } from '@/utils/approval/claimClosing';
import { isInventoryReplenishmentType } from '@/utils/approval/inventoryReplenishment';
import { isStorageMaterialOrderType } from '@/utils/approval/storageMaterialOrder';
import { isSupplierEvaluationType } from '@/utils/approval/supplierEvaluation';
import { isPurchaseRequisitionType } from '@/utils/approval/purchaseRequisition';
import { isCostQuotationType } from '@/utils/approval/costQuotation';
import { isSalesContractType } from '@/utils/approval/salesContract';
import { isCreditNoteType } from '@/utils/approval/creditNote';
import { isClaimNoteType } from '@/utils/approval/claimNote';
import {
  buildGroupSalesMultiAttributes,
  isGroupSalesMultiType,
  type GroupSalesMultiState,
} from '@/utils/approval/groupSalesMulti';
import { isSapBudgetIncreaseType } from '@/utils/approval/sapBudgetIncrease';
import { isSapCollateralType } from '@/utils/approval/sapCollateralChange';
import { isAccountingBpType } from '@/utils/approval/accountingBusinessPartner';
import { isInfluencerPplType } from '@/utils/approval/influencerPpl';
import { isInfluencerMonthlyReportType } from '@/utils/approval/influencerMonthlyReport';
import { isProductionCostType } from '@/utils/approval/productionCost';
import { isExternalLoanType } from '@/utils/approval/externalLoan';
import { isSapBondStatusType } from '@/utils/approval/sapBondStatus';
import { isSaTravelType } from '@/utils/approval/saTravelExpense';
import { isFncoPriceType } from '@/utils/approval/fncoPriceSupply';
import {
  buildDiscountVoucherAttributes,
  isDiscountVoucherType,
  type DiscountVoucherState,
} from '@/utils/approval/discountVoucher';
import {
  buildExpenseVoucherAttributes,
  isExpenseVoucherType,
  type ExpenseVoucherState,
} from '@/utils/approval/expenseVoucher';
import { buildExpenseAttributes } from '@/utils/approval/expensePreApprovalHtml';
import { encodeSecurityLevel } from '@/utils/approval/securityLevel';
import { isEmptyContent } from '@/utils/editor';
import type {
  ApprovalAttributes,
  ApprovalStatus,
  Attachment,
  CreateApprovalApprover,
  CreateApprovalAttachment,
  CreateApprovalRequest,
} from '@/types/approval';

/**
 * 결재라인 한 명의 입력 단위.
 * - passportId: ApprovalLineSection 의 FlowMemberData.userId 에 해당.
 */
export type CreatePayloadApprover = {
  passportId: string;
};

/**
 * 결재라인 입력
 */
export type ApprovalLineInput = {
  approvers: CreatePayloadApprover[];
  cooperation?: {
    members: CreatePayloadApprover[];
    insertIndex: number;
    ordered: boolean;
  };
};

/**
 * 폼 state 스냅샷.
 *
 *  - type=1 기본: null 또는 빈 객체
 *  - type=32 연차: leaveType / leaveSubtype / startsAt / startsUnit / endsAt /
 *                  endsUnit / workStartHour / workEndHour
 *  - type=99 SA 전표: saVoucher 스냅샷으로 sapDocumentNo / zSum / content(전표 표 HTML) 생성
 *  - type=205 사전 비용: expensePreApproval 로 content(견적 HTML) / data(비용구분·업체·지급 총액) 생성
 *  - type=233 매장 OPEN: openConstructionCost 로 aiPipelineId / content(리포트 HTML) / data(분석 결과 원문)
 *  - type=221 F&CO 마케팅 사전: fncoPreMarketing 으로 analysisId / content(레거시 본문 HTML). data 는 서버가 채움
 *  - type=212 마케팅 비용: marketingExpense 로 analysisId / content(레거시 본문 HTML). data·internalOrderNumber 는 서버가 채움
 *  - type=213 ENT. 프로젝트(비용): entExpense 로 사용 예산·지급 정보·분류별 집계(평탄형)
 *  - type=183 작업사양서: workSpecChecklist 로 checklist(체크 항목 문구 배열)
 *  - type=209 F&CO 쇼룸 출고: fncoShowRoomRequestNumber 로 requestNumber
 *  - type=202/203 F&CO 신제품 발주·운영제품 재발주: fncoPurchaseRequestNumber 로 requestNumber
 *  - type=93 차량유지비 신청: vehicleCostContent 로 content(임직원 정보 HTML)
 *  - type=196 퇴직원: resignation 으로 content(사유·서약 HTML)·data(레거시 CL02 JSON)
 *  - type=240 F&CO 장기근속 포상휴가: longServiceLeave 로 content(신청자·서약 HTML)·startsAt·endsAt·data(레거시 CL02 JSON). [1주년] 은 기간 없음
 *  - type=204 신규입사자 법인카드: newCorporateCard 로 content(서약 HTML)·신청 항목
 *  - type=200·201 거래처 신규/변경 HK·TW: bpRegistration 으로 content(업체소싱정보 HTML)·data(레거시 CL02 JSON)
 *  - type=113 법인인감 날인 신청: corporateSeal 로 company·seal·lawConfirm(표시 글자)
 *  - type=157 SAP 중간 관리 수수료 정산: shopManagerFee 로 content(집계표 HTML)·data(SAP 원문)
 *  - type=167 SAP 도급비 수수료 정산: sapContractFee 로 content(집계표 HTML)·year·month·data
 *  - type=169 SERP 재고실사 조정: serpStockAdjustment 로 typeCode·sequence·content(표 HTML)·data(EAI 원문)
 *  - type=162 SERP 매장 변경: serpStoreChange 로 typeCode·sequence·content(표 HTML)·data(EAI 원문)
 *  - type=161 SERP 매장 개점: serpStoreOpen 으로 typeCode·sequence·content(표 HTML)·data(EAI 원문)
 *  - type=171 SERP 매장반품 요청: serpStoreReturn 으로 typeCode·sequence·content(표 HTML)·data(요약+상세)
 *  - type=163 SERP 여신유예신청: serpLendingDelay 로 typeCode·sequence·content(표 HTML)·data(EAI 원문)
 *  - type=181 SERP 폐점 정산: serpStoreClosure 로 typeCode·sequence·content(표 HTML)·data(EAI 원문)
 *  - type=182 SERP 중간관리자 교체 정산: serpIntermediaryReplace 로 typeCode·sequence·content(표 HTML)·data(EAI 원문)
 *  - type=160 SERP 판매가 조정: serpSellingPrice 로 typeCode·sequence·content(표 HTML)·data(EAI 원문)
 *  - type=159 SERP 시즌운영계획: serpSeasonPlan 으로 typeCode·sequence·content(표 HTML)·data(EAI 원문)
 *  - type=210 SERP 재고실사 조정(다중): serpBulkStockAdjustment 로 typeCode·content(표·매장별 상세 HTML)·data(sequence·EAI 원문 문자열)
 *  - type=174 SERP 시즌마감 조정: serpSeasonEnd 로 typeCode·sequence·content(표·정산내역 HTML)·data(본문 원문+SHOP_LIST)
 *  - type=170 SERP AP 재고 이관 요청: serpApStockTransfer 로 typeCode·sequence·content(표 HTML)·data(EAI 원문)
 *  - type=81 (구)근저당설정비용 지급: mortgageFee 로 매장·담보·비용 16항목
 *  - type=8 (구)매장 OPEN: storeOpen 으로 매장 키·입력값. 대리점·양식 미선택은 attributes 없음(null)
 *  - type=206·207 법인카드 한도증액·재발급: corporateCardRequest 로 신청자·카드번호·신청 항목
 *  - type=188 수습평가서: probationEvaluation 으로 content(대상자 정보 HTML)·targetEmployeeNumber
 *  - type=215 Sales 브랜드 권한 변경: salesBrandPermission 으로 content(체크 목록 HTML)·addedBrands·removedBrands
 *  - type=61 단체판매 진행: groupSales 로 brandCode·shopCode·sequence·brandName·content(작성 시점 내역표 HTML)
 *  - type=131 단체판매 진행(다중): groupSalesMulti 로 group(레거시 JSON 모양)·content(작성 시점 내역 HTML)
 *  - type=83 (구)마케팅 건별 진행: marketingCase 로 비용 건 키 4개·content(작성 시점 표·견적 상세 HTML), 새로 고른 건은 최상위 marketingCase 로도
 *  - type=187 SAP 예산증액 신청: sapBudgetIncrease 로 yearMonth·costElement·amount·content(표 HTML)
 *  - type=154·155 SAP 담보 신규·변경: sapCollateralChange 로 sapKey·content(표 HTML)
 *  - type=101 회계거래처 신규/변경: accountingBp 로 sapDocumentNumber·content(거래처 표·업체소싱정보 HTML)·companyData(레거시 CL02)
 *  - type=135 인플루언서 PPL 정산: influencerPpl 로 documentSeq·brand·season·content(표 HTML)·detailInfo(그래프·상세보기 데이터)
 *  - type=108 인플루언서 월마감 보고서: influencerMonthlyReport 로 documentSequence·content(표 HTML)
 *  - type=216 생산비 마감(회계): productionCost 로 groupwareDocumentNumber·content(표 HTML)·data(SAP 집계·차이내역 내용·본문)
 *  - type=217 외담대 발행: externalLoan 으로 issueDate·groupwareDocumentNumber·items(SAP 발행내역 그대로)
 *  - type=185 SAP 채권현황: sapBondStatus 로 content(표 HTML)·연월·브랜드·SAP 원문
 *  - type=177 SA 신규 출장비 정산: saTravelExpense 로 sapDocumentNo·zType(base64)·content(표 HTML)
 *  - type=179 SA 신규 출장비 사전 신청: 177 + data(SAP 원문)
 *  - type=197~199 가격 등록 공급가·할인율·판매가 요청: fncoPriceSupply 로 requestNumber·content(표 HTML)·data(SAP 원문)
 *  - type=68 할인권 발행: discountVoucher 그대로(브랜드·매장·할인권 번호·표 HTML)
 *  - type=111 지출결의서: expenseVoucher 로 지급 정보·거래처 정보·필수 확인·지급 내역(합계는 서버 계산)
 *  - type=117/118 계약: contractIds·contractContent(계약 표 HTML)로 attributes 생성(attributes 직접 입력은 무시)
 *  - type=226/227/232/237/238/239 취소: cancelTarget 스냅샷의 approvalId 만 attributes 로
 */
export type ApprovalFormSnapshot = {
  apvType: number;
  subject: string;
  content: string;
  secuLevel: string; // RHF secuLevel — 라디오 문자열 ('1'|'5'|'7'). 9(비공개)는 7+restrictReferrer 로 인코딩 (utils/approval/securityLevel)
  restrictReferrer: boolean; // 상신 후 참조인 추가 제한. true 면 저장 시 securityLevel 을 9(비공개)로 인코딩.
  approvalLine: ApprovalLineInput;
  referrers: Referrer[];
  attachments: Attachment[];
  showImagesBelowContent: boolean; // 첨부 이미지를 본문 하단에 표시 여부 (레거시 image_display_yn)
  attributes?: ApprovalAttributes | null;
  /** 계약 품의 연결 계약 ID(순서 보존). 계약 유형이 아니면 무시 */
  contractIds?: number[];
  /** 계약 품의 작성 시점 계약 표 HTML. 계약 유형이 아니면 무시 */
  contractContent?: string | null;
  /** 취소 품의 취소 대상 원 품의 스냅샷. 취소 유형이 아니면 무시 */
  cancelTarget?: CancelTargetSnapshot | null;
  /** SA 전표(99) 스냅샷. 99 가 아니면 무시 */
  saVoucher?: SaVoucherSnapshot | null;
  /** 사전 비용 품의(205) 폼 상태. 205 가 아니면 무시 */
  expensePreApproval?: ExpensePreApprovalState | null;
  /** 매장 OPEN 품의(233) 분석 결과. 233 이 아니면 무시 */
  /** 매장 OPEN 품의(233) attributes — content(리포트 HTML)는 화면 컴포넌트로 만들어 넘김. 233 이 아니면 무시 */
  openConstructionCost?: OpenConstructionCostAttributes | null;
  /** F&CO 마케팅 사전 품의(221) attributes(analysisId·content). 221 이 아니면 무시 */
  fncoPreMarketing?: ApprovalAttributes | null;
  /** 마케팅 비용 품의(212) attributes(analysisId·content). 212 가 아니면 무시 */
  marketingExpense?: ApprovalAttributes | null;
  /** ENT. 프로젝트(비용) 품의(213) 폼 상태. 213 이 아니면 무시 */
  entExpense?: EntExpenseState | null;
  /** 작업사양서(183) 체크 항목 — 한국어 문구. 183 이 아니면 무시 */
  workSpecChecklist?: string[] | null;
  /** F&CO 쇼룸 출고 신청(209) EAS 요청번호. 209 가 아니면 무시 */
  fncoShowRoomRequestNumber?: string | null;
  /** F&CO 신제품 발주(202)·운영제품 재발주(203) EAS 요청번호. 202·203 이 아니면 무시 */
  fncoPurchaseRequestNumber?: string | null;
  /** 차량유지비 신청(93) 임직원 정보 HTML. 93 이 아니면 무시 */
  vehicleCostContent?: string | null;
  /** 퇴직원(196) attributes(content·data). 호출부가 HTML 까지 만들어 넘김. 196 이 아니면 무시 */
  resignation?: ApprovalAttributes | null;
  /** F&CO 장기근속 포상휴가(240) attributes. 호출부가 HTML 까지 만들어 넘김. 240 이 아니면 무시 */
  longServiceLeave?: ApprovalAttributes | null;
  /** 신규입사자 법인카드(204) attributes. 호출부가 서약 HTML 까지 만들어 넘김. 204 가 아니면 무시 */
  newCorporateCard?: ApprovalAttributes | null;
  /** 거래처 신규/변경 HK·TW(200·201) attributes(content·data). 호출부가 HTML 까지 만들어 넘김. 200·201 이 아니면 무시 */
  bpRegistration?: ApprovalAttributes | null;
  /** 법인인감 날인 신청(113) 선택값. 113 이 아니면 무시 */
  corporateSeal?: CorporateSealFormState | null;
  /** SAP 중간 관리 수수료 정산(157) 조회 결과. 157 이 아니면 무시 */
  shopManagerFee?: ShopManagerFeeState | null;
  /** SAP 도급비 수수료 정산(167) attributes. 호출부가 표 HTML 까지 만들어 넘김. 167 이 아니면 무시 */
  sapContractFee?: ApprovalAttributes | null;
  /** SERP 재고실사 조정(169) attributes. 호출부가 표 HTML 까지 만들어 넘김. 169 가 아니면 무시 */
  serpStockAdjustment?: ApprovalAttributes | null;
  /** SERP 매장 변경(162) attributes. 호출부가 표 HTML 까지 만들어 넘김. 162 가 아니면 무시 */
  serpStoreChange?: ApprovalAttributes | null;
  /** SERP 매장 개점(161) attributes. 호출부가 표 HTML 까지 만들어 넘김. 161 이 아니면 무시 */
  serpStoreOpen?: ApprovalAttributes | null;
  /** SERP 매장반품 요청(171) attributes. 호출부가 표 HTML 까지 만들어 넘김. 171 이 아니면 무시 */
  serpStoreReturn?: ApprovalAttributes | null;
  /** SERP 여신유예신청(163) attributes. 호출부가 표 HTML 까지 만들어 넘김. 163 이 아니면 무시 */
  serpLendingDelay?: ApprovalAttributes | null;
  /** SERP 폐점 정산(181) attributes. 호출부가 표 HTML 까지 만들어 넘김. 181 이 아니면 무시 */
  serpStoreClosure?: ApprovalAttributes | null;
  /** SERP 중간관리자 교체 정산(182) attributes. 호출부가 표 HTML 까지 만들어 넘김. 182 가 아니면 무시 */
  serpIntermediaryReplace?: ApprovalAttributes | null;
  /** SERP 판매가 조정(160) attributes. 호출부가 표 HTML 까지 만들어 넘김. 160 이 아니면 무시 */
  serpSellingPrice?: ApprovalAttributes | null;
  /** SERP 시즌운영계획(159) attributes. 호출부가 표 HTML 까지 만들어 넘김. 159 가 아니면 무시 */
  serpSeasonPlan?: ApprovalAttributes | null;
  /** SERP 재고실사 조정(다중)(210) attributes. 호출부가 표 HTML 까지 만들어 넘김. 210 이 아니면 무시 */
  serpBulkStockAdjustment?: ApprovalAttributes | null;
  /** SERP 시즌마감 조정(174) attributes. 호출부가 표 HTML 까지 만들어 넘김. 174 가 아니면 무시 */
  serpSeasonEnd?: ApprovalAttributes | null;
  /** SERP AP 재고 이관 요청(170) attributes. 호출부가 표 HTML 까지 만들어 넘김. 170 이 아니면 무시 */
  serpApStockTransfer?: ApprovalAttributes | null;
  /** (구)근저당설정비용 지급(81) 입력값. 81 이 아니면 무시 */
  mortgageFee?: MortgageFeeFormState | null;
  storeOpen?: StoreOpenFormState | null;
  /** 법인카드 한도증액·재발급(206·207) 입력값. 206·207 이 아니면 무시 */
  corporateCardRequest?: CorporateCardRequestFormState | null;
  /** 연차휴가(32) 입력값. 32 가 아니면 무시 */
  annualLeaveRequest?: AnnualLeaveRequestFormState | null;
  /** 수습평가서(188) 확인한 대상자. 188 이 아니면 무시 */
  probationEvaluation?: ProbationEvaluationState | null;
  /** Sales 브랜드 권한 변경(215) attributes. 호출부가 HTML 까지 만들어 넘김. 215 가 아니면 무시 */
  salesBrandPermission?: ApprovalAttributes | null;
  /** 단체판매 진행(61) 확정 건. 61 이 아니면 무시 */
  groupSales?: GroupSalesState | null;
  /** 단체판매 진행(다중)(131) 확정 건들. 131 이 아니면 무시 */
  groupSalesMulti?: GroupSalesMultiState | null;
  /** (구)마케팅 건별 진행(83) 확정 건. 83 이 아니면 무시 */
  marketingCase?: MarketingCaseState | null;
  /** GERP 생산거래처 신규/변경(120) attributes. 120 이 아니면 무시 */
  productionVendor?: ApprovalAttributes | null;
  /** GERP PLM(123) attributes. 123 이 아니면 무시 */
  productLifecycle?: ApprovalAttributes | null;
  /** GERP STYLE MASTER 가격 변경(128) attributes. 128 이 아니면 무시 */
  styleMasterPrice?: ApprovalAttributes | null;
  /** GERP 생산비 마감(124) attributes. 124 가 아니면 무시 */
  productionCostClosing?: ApprovalAttributes | null;
  /** GERP 클레임 마감(186) attributes. 186 이 아니면 무시 */
  claimClosing?: ApprovalAttributes | null;
  /** GERP 저장품 추가발주(184) attributes. 184 가 아니면 무시 */
  inventoryReplenishment?: ApprovalAttributes | null;
  /** GERP 저장부자재 발주(125) attributes. 125 가 아니면 무시 */
  storageMaterialOrder?: ApprovalAttributes | null;
  /** GERP 협력사 평가(127) attributes. 127 이 아니면 무시 */
  supplierEvaluation?: ApprovalAttributes | null;
  /** GERP PR(제상품)(122)·PO(합의납기일/생산처)(129) attributes. 두 유형이 아니면 무시 */
  purchaseRequisition?: ApprovalAttributes | null;
  /** GERP 원가 견적서(121) attributes. 121 이 아니면 무시 */
  costQuotation?: ApprovalAttributes | null;
  /** GERP Sales Contract(136) attributes. 136 이 아니면 무시 */
  salesContract?: ApprovalAttributes | null;
  /** GERP CREDIT NOTE(156) attributes. 156 이 아니면 무시 */
  creditNote?: ApprovalAttributes | null;
  /** GERP CLAIM NOTE(166) attributes. 166 이 아니면 무시 */
  claimNote?: ApprovalAttributes | null;
  /** SAP 예산증액 신청(187) attributes. 호출부가 표 HTML 까지 만들어 넘김. 187 이 아니면 무시 */
  sapBudgetIncrease?: ApprovalAttributes | null;
  /** SAP 담보 신규(154)·변경(155) attributes. 호출부가 표 HTML 까지 만들어 넘김. 154·155 가 아니면 무시 */
  sapCollateralChange?: ApprovalAttributes | null;
  accountingBp?: ApprovalAttributes | null;
  /** 인플루언서 PPL 정산(135) attributes. 호출부가 표 HTML 까지 만들어 넘김. 135 가 아니면 무시 */
  influencerPpl?: ApprovalAttributes | null;
  /** 인플루언서 월마감 보고서(108) attributes. 호출부가 표 HTML 까지 만들어 넘김. 108 이 아니면 무시 */
  influencerMonthlyReport?: ApprovalAttributes | null;
  /** 생산비 마감(회계)(216) attributes. 호출부가 표 HTML·본문까지 넣어 넘김. 216 이 아니면 무시 */
  productionCost?: ApprovalAttributes | null;
  /** 외담대 발행(217) attributes. 217 이 아니면 무시 */
  externalLoan?: ApprovalAttributes | null;
  /** SAP 채권현황(185) attributes. 호출부가 표 HTML 까지 만들어 넘김. 185 가 아니면 무시 */
  sapBondStatus?: ApprovalAttributes | null;
  /** SA 신규 출장비 정산(177)·사전 신청(179) attributes. 호출부가 표 HTML 까지 만들어 넘김. 그 외 유형은 무시 */
  saTravelExpense?: ApprovalAttributes | null;
  /** 가격 등록 공급가·할인율·판매가 요청(197~199) attributes. 호출부가 표 HTML 까지 만들어 넘김. 다른 유형은 무시 */
  fncoPriceSupply?: ApprovalAttributes | null;
  /** 할인권 발행(68) 확정 할인권. 68 이 아니면 무시 */
  discountVoucher?: DiscountVoucherState | null;
  /** 지출결의서(111) 입력값. 111 이 아니면 무시 */
  expenseVoucher?: ExpenseVoucherState | null;
};

/**
 * 결재라인 입력
 */
const buildOrderedApprovers = (
  line: ApprovalLineInput,
): {
  approvers: CreateApprovalApprover[];
  coApprovers: CreateApprovalApprover[];
} => {
  const approvers: CreateApprovalApprover[] = [];
  const coApprovers: CreateApprovalApprover[] = [];

  const coopMembers = line.cooperation?.members ?? [];
  const hasCoop = coopMembers.length > 0;

  if (!hasCoop) {
    line.approvers.forEach((a, i) => {
      approvers.push({ passportId: a.passportId, order: i + 1 });
    });
    return { approvers, coApprovers };
  }

  const insertIndex = Math.max(
    0,
    Math.min(
      line.approvers.length,
      line.cooperation?.insertIndex ?? line.approvers.length,
    ),
  );
  const ordered = line.cooperation?.ordered ?? true;

  let order = 1;

  for (let i = 0; i < insertIndex; i += 1) {
    approvers.push({ passportId: line.approvers[i].passportId, order });
    order += 1;
  }

  if (ordered) {
    coopMembers.forEach((m) => {
      coApprovers.push({ passportId: m.passportId, order });
      order += 1;
    });
  } else {
    const sharedOrder = order;
    coopMembers.forEach((m) => {
      coApprovers.push({ passportId: m.passportId, order: sharedOrder });
    });
    order += 1;
  }

  for (let i = insertIndex; i < line.approvers.length; i += 1) {
    approvers.push({ passportId: line.approvers[i].passportId, order });
    order += 1;
  }

  return { approvers, coApprovers };
};

/**
 * 첨부 항목
 * - fileId 가 있으면 기존 유지 ({fileId}) 로 전송 → 서버가 삭제 처리하지 않음
 * - 없으면 신규 업로드 ({fileUuid}) 로 전송
 * - 둘 다 비어있는 항목은 잘못된 상태이므로 호출부에서 제외하거나 빈 결과를 받음
 */
const toAttachmentPayload = (
  a: Attachment,
): CreateApprovalAttachment | null => {
  if (a.fileId) return { fileId: a.fileId };
  if (a.fileUuid) return { fileUuid: a.fileUuid };
  return null;
};

// 폰트(패밀리) 변경 기능 제거에 맞춰, 저장 콘텐츠의 인라인 font-family 를 제거해 기본 폰트(Pretendard)로 통일.
// 복붙/v1 등 어떤 경로로 들어온 font-family 든 저장 결과엔 남지 않음 → 조회화면도 기본 폰트로 일치.
// (편집화면은 CSS 로 이미 기본 폰트 표시. 조회만 하는 미편집 v1 문서는 원본 유지 — 편집·저장 거쳐야 정리됨.)
export const stripFontFamily = (content: string): string => {
  if (typeof content !== 'string' || typeof DOMParser === 'undefined') {
    return content;
  }
  const doc = new DOMParser().parseFromString(content, 'text/html');
  doc.querySelectorAll('[style]').forEach((el) => {
    const element = el as HTMLElement;
    if (element.style.fontFamily) {
      element.style.removeProperty('font-family');
      if (!element.getAttribute('style')?.trim()) {
        element.removeAttribute('style');
      }
    }
  });
  return doc.body.innerHTML;
};

// 에디터는 비어 있어도 `<p><br></p>` 를 값으로 가져 trim 만으론 빈 본문이 저장됨
const trimContent = (content: string): string | null => {
  if (typeof content !== 'string') return null;
  return isEmptyContent(content) ? null : content;
};

// 계약 품의는 attributes.content 가 본문과 같은 값(서버 필수)이라 본문 정리 결과를 그대로 넣음
const resolveAttributes = (
  snapshot: ApprovalFormSnapshot,
  content: string | null,
): ApprovalAttributes | null => {
  if (isContractApprovalType(snapshot.apvType)) {
    return buildContractAttributes(
      content,
      snapshot.contractIds ?? [],
      snapshot.contractContent ?? null,
    );
  }
  if (isCancelApprovalType(snapshot.apvType)) {
    return buildCancelAttributes(snapshot.cancelTarget);
  }
  if (isSaVoucherApprovalType(snapshot.apvType)) {
    return buildSaVoucherAttributes(snapshot.saVoucher);
  }
  if (isExpensePreApprovalType(snapshot.apvType)) {
    return buildExpenseAttributes(
      snapshot.expensePreApproval,
      snapshot.subject,
    );
  }
  if (isOpenConstructionCostType(snapshot.apvType)) {
    return snapshot.openConstructionCost ?? null;
  }
  if (isFncoPreMarketingType(snapshot.apvType)) {
    return snapshot.fncoPreMarketing ?? null;
  }
  if (isMarketingExpenseType(snapshot.apvType)) {
    return snapshot.marketingExpense ?? null;
  }
  if (isEntExpenseType(snapshot.apvType)) {
    return buildEntExpenseAttributes(snapshot.entExpense, snapshot.subject);
  }
  if (isWorkSpecificationType(snapshot.apvType)) {
    return buildWorkSpecificationAttributes(snapshot.workSpecChecklist);
  }
  if (isFncoShowRoomType(snapshot.apvType)) {
    return buildFncoShowRoomAttributes(
      snapshot.fncoShowRoomRequestNumber ?? null,
    );
  }
  if (isFncoProductPurchaseType(snapshot.apvType)) {
    return buildFncoProductPurchaseAttributes(
      snapshot.fncoPurchaseRequestNumber ?? null,
    );
  }
  if (isVehicleCostType(snapshot.apvType)) {
    return buildVehicleCostAttributes(snapshot.vehicleCostContent);
  }
  if (isResignationType(snapshot.apvType)) {
    return snapshot.resignation ?? null;
  }
  if (isLongServiceLeaveType(snapshot.apvType)) {
    return snapshot.longServiceLeave ?? null;
  }
  if (isNewCorporateCardType(snapshot.apvType)) {
    return snapshot.newCorporateCard ?? null;
  }
  if (isBpRegistrationType(snapshot.apvType)) {
    return snapshot.bpRegistration ?? null;
  }
  if (isCorporateSealType(snapshot.apvType)) {
    return buildCorporateSealAttributes(snapshot.corporateSeal);
  }
  if (isShopManagerFeeType(snapshot.apvType)) {
    return buildShopManagerFeeAttributes(snapshot.shopManagerFee);
  }
  if (isSapContractFeeType(snapshot.apvType)) {
    return snapshot.sapContractFee ?? null;
  }
  if (isSerpStockAdjustmentType(snapshot.apvType)) {
    return snapshot.serpStockAdjustment ?? null;
  }
  if (isSerpStoreChangeType(snapshot.apvType)) {
    return snapshot.serpStoreChange ?? null;
  }
  if (isSerpStoreOpenType(snapshot.apvType)) {
    return snapshot.serpStoreOpen ?? null;
  }
  if (isSerpStoreReturnType(snapshot.apvType)) {
    return snapshot.serpStoreReturn ?? null;
  }
  if (isSerpLendingDelayType(snapshot.apvType)) {
    return snapshot.serpLendingDelay ?? null;
  }
  if (isSerpStoreClosureType(snapshot.apvType)) {
    return snapshot.serpStoreClosure ?? null;
  }
  if (isSerpIntermediaryReplaceType(snapshot.apvType)) {
    return snapshot.serpIntermediaryReplace ?? null;
  }
  if (isSerpSellingPriceType(snapshot.apvType)) {
    return snapshot.serpSellingPrice ?? null;
  }
  if (isSerpSeasonPlanType(snapshot.apvType)) {
    return snapshot.serpSeasonPlan ?? null;
  }
  if (isSerpBulkStockAdjustmentType(snapshot.apvType)) {
    return snapshot.serpBulkStockAdjustment ?? null;
  }
  if (isSerpSeasonEndType(snapshot.apvType)) {
    return snapshot.serpSeasonEnd ?? null;
  }
  if (isSerpApStockTransferType(snapshot.apvType)) {
    return snapshot.serpApStockTransfer ?? null;
  }
  if (isMortgageFeeType(snapshot.apvType)) {
    return buildMortgageFeeAttributes(snapshot.mortgageFee);
  }
  if (isStoreOpenType(snapshot.apvType)) {
    return buildStoreOpenAttributes(snapshot.storeOpen);
  }
  if (isCorporateCardRequestType(snapshot.apvType)) {
    return buildCorporateCardRequestAttributes(
      snapshot.apvType,
      snapshot.corporateCardRequest,
    );
  }
  if (isAnnualLeaveRequestType(snapshot.apvType)) {
    return buildAnnualLeaveRequestAttributes(snapshot.annualLeaveRequest);
  }
  if (isProbationEvaluationType(snapshot.apvType)) {
    return buildProbationEvaluationAttributes(snapshot.probationEvaluation);
  }
  if (isSalesBrandPermissionType(snapshot.apvType)) {
    return snapshot.salesBrandPermission ?? null;
  }
  if (isGroupSalesType(snapshot.apvType)) {
    return buildGroupSalesAttributes(snapshot.groupSales);
  }
  if (isGroupSalesMultiType(snapshot.apvType)) {
    return buildGroupSalesMultiAttributes(snapshot.groupSalesMulti);
  }
  if (isMarketingCaseType(snapshot.apvType)) {
    return buildMarketingCaseAttributes(snapshot.marketingCase);
  }
  if (isProductionVendorType(snapshot.apvType)) {
    return snapshot.productionVendor ?? null;
  }
  if (isProductLifecycleType(snapshot.apvType)) {
    return snapshot.productLifecycle ?? null;
  }
  if (isStyleMasterPriceType(snapshot.apvType)) {
    return snapshot.styleMasterPrice ?? null;
  }
  if (isProductionCostClosingType(snapshot.apvType)) {
    return snapshot.productionCostClosing ?? null;
  }
  if (isClaimClosingType(snapshot.apvType)) {
    return snapshot.claimClosing ?? null;
  }
  if (isInventoryReplenishmentType(snapshot.apvType)) {
    return snapshot.inventoryReplenishment ?? null;
  }
  if (isStorageMaterialOrderType(snapshot.apvType)) {
    return snapshot.storageMaterialOrder ?? null;
  }
  if (isSupplierEvaluationType(snapshot.apvType)) {
    return snapshot.supplierEvaluation ?? null;
  }
  if (isPurchaseRequisitionType(snapshot.apvType)) {
    return snapshot.purchaseRequisition ?? null;
  }
  if (isCostQuotationType(snapshot.apvType)) {
    return snapshot.costQuotation ?? null;
  }
  if (isSalesContractType(snapshot.apvType)) {
    return snapshot.salesContract ?? null;
  }
  if (isCreditNoteType(snapshot.apvType)) {
    return snapshot.creditNote ?? null;
  }
  if (isClaimNoteType(snapshot.apvType)) {
    return snapshot.claimNote ?? null;
  }
  if (isSapBudgetIncreaseType(snapshot.apvType)) {
    return snapshot.sapBudgetIncrease ?? null;
  }
  if (isSapCollateralType(snapshot.apvType)) {
    return snapshot.sapCollateralChange ?? null;
  }
  if (isAccountingBpType(snapshot.apvType)) {
    return snapshot.accountingBp ?? null;
  }
  if (isInfluencerPplType(snapshot.apvType)) {
    return snapshot.influencerPpl ?? null;
  }
  if (isInfluencerMonthlyReportType(snapshot.apvType)) {
    return snapshot.influencerMonthlyReport ?? null;
  }
  if (isProductionCostType(snapshot.apvType)) {
    return snapshot.productionCost ?? null;
  }
  if (isExternalLoanType(snapshot.apvType)) {
    return snapshot.externalLoan ?? null;
  }
  if (isSapBondStatusType(snapshot.apvType)) {
    return snapshot.sapBondStatus ?? null;
  }
  if (isSaTravelType(snapshot.apvType)) {
    return snapshot.saTravelExpense ?? null;
  }
  if (isExpenseVoucherType(snapshot.apvType)) {
    return buildExpenseVoucherAttributes(snapshot.expenseVoucher);
  }
  if (isFncoPriceType(snapshot.apvType)) {
    return snapshot.fncoPriceSupply ?? null;
  }
  if (isDiscountVoucherType(snapshot.apvType)) {
    return buildDiscountVoucherAttributes(snapshot.discountVoucher);
  }
  return snapshot.attributes ?? null;
};

const approvalFormToCreatePayload = (
  snapshot: ApprovalFormSnapshot,
  status: ApprovalStatus,
): CreateApprovalRequest => {
  const { approvers, coApprovers } = buildOrderedApprovers(
    snapshot.approvalLine,
  );
  const content = trimContent(stripFontFamily(snapshot.content));
  // 서버는 결재선·참조인을 한 사람당 한 줄로 저장해 겹치면 422 — 결재선에 든 사람은 참조인에서 뺀다
  const lineMemberIds = new Set(
    [...approvers, ...coApprovers].map((a) => a.passportId),
  );
  const referrers = snapshot.referrers.filter((r) => !lineMemberIds.has(r.id));

  return {
    type: snapshot.apvType,
    subject: snapshot.subject,
    content,
    status,
    securityLevel: encodeSecurityLevel(
      snapshot.secuLevel,
      snapshot.restrictReferrer,
    ),
    approvers,
    coApprovers: coApprovers.length > 0 ? coApprovers : null,
    referrers:
      referrers.length > 0
        ? referrers.map((r) => ({ passportId: r.id }))
        : null,
    attributes: resolveAttributes(snapshot, content),
    attachments: (() => {
      const items = snapshot.attachments
        .map(toAttachmentPayload)
        .filter((x): x is CreateApprovalAttachment => x !== null);
      return items.length > 0 ? items : null;
    })(),
    showImagesBelowContent: snapshot.showImagesBelowContent,
    ...(isMarketingCaseType(snapshot.apvType) && {
      marketingCase: buildMarketingCaseLink(snapshot.marketingCase),
    }),
  };
};

export default approvalFormToCreatePayload;
